import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { CkdInputOption, DyslipidemiaInputOption, InputOption } from '@app/models/input-option';
import { ControlTypeEnum, SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { LimitsInputComponent } from '@app/shared/inputs/limits-input/limits-input.component';
import { RadioInputGroupComponent } from '@app/shared/inputs/radio-input-group/radio-input-group.component';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { asapScheduler, first, skip } from 'rxjs';

@Component({
  selector: 'app-ckd',
  templateUrl: './ckd.component.html',
  styleUrls: ['./ckd.component.css']
})
export class CkdComponent implements OnInit {

  @ViewChild('childDiagnosisComponent') childDiagnosisComponent: RadioInputGroupComponent;
  @ViewChild('childLimitsComponent') childLimitsComponent: LimitsInputComponent;

  @Input() indication: IndicationDefinition;
  ckdHemoglobinLimitsIndication: IndicationDefinition;
  
  allIndications: IndicationGroup[] = [];

  hasErrors = false;

  childDiagnosisSelectionModel: SelectionsModel;
  childHemoglobinLimitsSelectionModel: SelectionsModel[];

  equationVariableName = 'equation';
  gfrLowerLimitsVariableName = 'GFR_LowerLimits';
  gfrUpperLimitsVariableName = 'GFR_UpperLimits';
  uacrLowerLimitsVariableName = 'UACR_LowerLimits';
  uacrUpperLimitsVariableName = 'UACR_UpperLimits';
  ckdMainCriteriaKeys = [this.gfrLowerLimitsVariableName, this.gfrUpperLimitsVariableName, this.uacrLowerLimitsVariableName, this.uacrUpperLimitsVariableName];
  includeHemoglobinLimitsVariableName = 'includeHemoglobinLimits';

  equationRadioInputOptions: InputOption[] = [
    {name: 'mdrd4', displayName: 'MDRD-4', order: 1, defaultValue: 'mdrd4'},
    {name: 'ckdepi', displayName: 'CKD-EPI', order: 2, defaultValue: 'mdrd4'}
  ];

  includeHemoglobinRadioInputOptions: InputOption[] = [
    {name: 'n', displayName: 'No', order: 1, defaultValue: 'n'},
    {name: 'y', displayName: 'Yes', order: 2, defaultValue: 'n'}
  ];

  ckdCriteriaOptions: CkdInputOption[] = [
    {
      lowerLimits: {
        name: this.gfrLowerLimitsVariableName,
        displayName: 'GFR Lower Limits',
        defaultValue: '60',
        order: 1,
        measurement: 'mL/min per 1.73 m²'
      },
      upperLimits: {
        name: this.gfrUpperLimitsVariableName,
        displayName: 'GFR Upper Limits',
        defaultValue: '150',
        order: 2,
        measurement: 'mL/min per 1.73 m²'
      }
    },
    {
      lowerLimits: {
        name: this.uacrLowerLimitsVariableName,
        displayName: 'UACR Lower Limits',
        defaultValue: '0',
        order: 1,
        measurement: 'mg/g'
      },
      upperLimits: {
        name: this.uacrUpperLimitsVariableName,
        displayName: 'UACR Upper Limits',
        defaultValue: '30',
        order: 2,
        measurement: 'mg/g'
      }
    }
  ];

  maleUpperLimitsDefault = 13.5;
  femaleUpperLimitsDefault = 12.0;

  ckdHemoglobinLimitsInputOptions: InputOption[] = [
    {name: 'maleUpperLimits', displayName: 'Hemoglobin Male Upper Limits', order: 1, defaultValue: this.maleUpperLimitsDefault, maxlength: 5, details: '(<)' },
    {name: 'femaleUpperLimits', displayName: 'Hemoglobin Female Upper Limits', order: 2, defaultValue: this.femaleUpperLimitsDefault, maxlength: 5, details: '(<)'}
  ];

  public formGroup: FormGroup = this.getDefaultFormGroup();

  constructor(
    private sharedService: SharedService,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    this.assignChildIndicationDetails();

    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });

    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.assignChildIndicationDetails();
        this.setIndicationSelections();
      }
    });
  }

  assignChildIndicationDetails() {
    this.ckdHemoglobinLimitsIndication = Object.assign({}, this.indication);
    this.ckdHemoglobinLimitsIndication.selections = this.ckdHemoglobinLimitsIndication.selections.filter(i => i.controlName === 'hemoglobinLimits');
    this.ckdHemoglobinLimitsIndication.displayName = 'Hemoglobin';
    this.ckdHemoglobinLimitsIndication.units = 'g/dL';
    this.ckdHemoglobinLimitsIndication.inputOptions = this.ckdHemoglobinLimitsInputOptions;
    if (this.ckdHemoglobinLimitsIndication.selections && this.ckdHemoglobinLimitsIndication.selections.length > 0) {
      this.childHemoglobinLimitsSelectionModel = this.ckdHemoglobinLimitsIndication.selections;
    }
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      asapScheduler.schedule(() => {

        // child diagnosis radio button group
        if (this.indication.selections.some(i => i.controlName === 'diagnosis')) {
          this.childDiagnosisComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'diagnosis').selectionValue);
        }

        if (this.formGroup) {
          // equation radio buttons
          if (this.indication.selections.some(i => i.controlName === this.equationVariableName)){
            this.formGroup.controls.equation.setValue(this.indication.selections.find(i => i.controlName === this.equationVariableName).selectionValue);
          }

          // inputs
          if (this.indication.selections.some(i => i.controlName === this.gfrLowerLimitsVariableName)){
            this.formGroup.controls.GFR_LowerLimits.setValue(this.indication.selections.find(i => i.controlName === this.gfrLowerLimitsVariableName).selectionValue);
          }
          if (this.indication.selections.some(i => i.controlName === this.gfrUpperLimitsVariableName)){
            this.formGroup.controls.GFR_UpperLimits.setValue(this.indication.selections.find(i => i.controlName === this.gfrUpperLimitsVariableName).selectionValue);
          }
          if (this.indication.selections.some(i => i.controlName === this.uacrLowerLimitsVariableName)){
            this.formGroup.controls.UACR_LowerLimits.setValue(this.indication.selections.find(i => i.controlName === this.uacrLowerLimitsVariableName).selectionValue);
          }
          if (this.indication.selections.some(i => i.controlName === this.uacrUpperLimitsVariableName)){
            this.formGroup.controls.UACR_UpperLimits.setValue(this.indication.selections.find(i => i.controlName === this.uacrUpperLimitsVariableName).selectionValue);
          }

          // include limits yes no radio buttons
          if (this.indication.selections.some(i => i.controlName === this.includeHemoglobinLimitsVariableName)){
            this.formGroup.controls.includeHemoglobinLimits.setValue(this.indication.selections.find(i => i.controlName === this.includeHemoglobinLimitsVariableName).selectionValue);
          }

          // limits
          if (this.indication.selections.some(i => i.controlName === 'maleUpperLimits')) {
            if (this.childLimitsComponent) {
              this.childLimitsComponent.overrideInputValue('maleUpperLimits', this.indication.selections.find(i => i.controlName === 'maleUpperLimits').selectionValue);
            }
          }
          if (this.indication.selections.some(i => i.controlName === 'femaleUpperLimits')) {
            if (this.childLimitsComponent) {
              this.childLimitsComponent.overrideInputValue('femaleUpperLimits', this.indication.selections.find(i => i.controlName === 'femaleUpperLimits').selectionValue);
            }
          }

        }
      
        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.childDiagnosisComponent.overrideInputValue('DU');
      this.setLimitsDefaults();
      this.formGroup = this.getDefaultFormGroup();
      this.updateSelections();
    });
  }

  setLimitsDefaults() {
    if (this.childLimitsComponent) {
      this.childLimitsComponent.overrideInputValue('maleUpperLimits', this.maleUpperLimitsDefault);
      this.childLimitsComponent.overrideInputValue('femaleUpperLimits', this.femaleUpperLimitsDefault);
    }
  }

  getDefaultFormGroup(): FormGroup {
    let form = { 
      equation: new FormControl(this.equationRadioInputOptions[0].defaultValue, [Validators.required])
    };
    for(let i = 0; i < this.ckdCriteriaOptions.length; i++) {
      form[this.ckdCriteriaOptions[i].lowerLimits.name] = new FormControl(this.ckdCriteriaOptions[i].lowerLimits.defaultValue, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ]);
      form[this.ckdCriteriaOptions[i].upperLimits.name] = new FormControl(this.ckdCriteriaOptions[i].upperLimits.defaultValue, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ]);
    }
    form[this.includeHemoglobinLimitsVariableName] = new FormControl(this.includeHemoglobinRadioInputOptions[0].defaultValue, [Validators.required])

    return this.formBuilder.group(form);
  }

  childSharedControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childDiagnosisSelectionModel = selectionModel;
    if (!this.showCriteriaSection()) {
      this.formGroup = this.getDefaultFormGroup();
    }
    this.updateSelections();
  }

  childSharedTextControlSelectionChangedHandler(selections: SelectionsModel[]) {
    this.childHemoglobinLimitsSelectionModel = selections;
    if (!this.showHemoglobinLimitsSection()) {
      this.formGroup = this.getDefaultFormGroup();
    }
    this.updateSelections();
  }

  showCriteriaSection(): boolean {
    const visible = this.childDiagnosisSelectionModel && 
    !vennHealthConstants.diagnosedOrTreatedOnlyCodes.includes(this.childDiagnosisSelectionModel.selectionValue);
    return visible;
  }

  showHemoglobinLimitsSection(): boolean {
    const includeHemoglobinLimitsFormControl = this.formGroup.controls.includeHemoglobinLimits;
    return includeHemoglobinLimitsFormControl && includeHemoglobinLimitsFormControl.value === 'y';
  }

  updateSelections(): void {
    let currentSelections: SelectionsModel[] = [];
    
    if (this.childDiagnosisSelectionModel) {
      currentSelections.push(this.childDiagnosisSelectionModel);
    }
    
    const equationFormControl = this.formGroup.controls.equation;
    const equationSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: this.equationVariableName,
      controlType: ControlTypeEnum.RADIO,
      controlDisplayName: "Equation",
      order: 1,
      selectionValue: equationFormControl.value,
      selectionDisplayName: this.equationRadioInputOptions.find(i => i.name === equationFormControl.value).displayName
    };
    currentSelections.push(equationSelection);
    Object.keys(this.formGroup.controls).forEach(key => {
      if (this.ckdMainCriteriaKeys.includes(key)) {
        const control = this.formGroup.get(key);
        const type = key.split('_')[1];
        if (type === 'LowerLimits') {
          const option = this.ckdCriteriaOptions.find(i => i.lowerLimits.name === key);
          const currentSelection: SelectionsModel = {
            indicationName: this.indication.name,
            controlName: key,
            controlType: ControlTypeEnum.TEXT,
            controlDisplayName: option.lowerLimits.displayName,
            order: option.lowerLimits.order,
            selectionValue: control.value,
            selectionDisplayName: option.lowerLimits.displayName 
          };
          currentSelections.push(currentSelection);
        } else { // upper limits
          const option = this.ckdCriteriaOptions.find(i => i.upperLimits.name === key);
          const currentSelection: SelectionsModel = {
            indicationName: this.indication.name,
            controlName: key,
            controlType: ControlTypeEnum.TEXT,
            controlDisplayName: option.upperLimits.displayName,
            order: option.upperLimits.order,
            selectionValue: control.value,
            selectionDisplayName: option.upperLimits.displayName 
          };
          currentSelections.push(currentSelection);
        }
      }
    });

    const includeHemoglobinLimitsFormControl = this.formGroup.controls.includeHemoglobinLimits;
    const includeHemoglobinLimitsSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: this.includeHemoglobinLimitsVariableName,
      controlType: ControlTypeEnum.RADIO,
      controlDisplayName: "Include Hemoglobin Limits",
      order: 10,
      selectionValue: includeHemoglobinLimitsFormControl.value,
      selectionDisplayName: this.includeHemoglobinRadioInputOptions.find(i => i.name === includeHemoglobinLimitsFormControl.value).displayName
    };
    currentSelections.push(includeHemoglobinLimitsSelection);

    if (this.childHemoglobinLimitsSelectionModel && this.showHemoglobinLimitsSection()) {
      currentSelections = currentSelections.concat(this.childHemoglobinLimitsSelectionModel);
    }

    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }
}
